import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { useIdioma } from '../../contexts/IdiomaContext';
import FooterSection from '../landing/sections/FooterSection';
import { usePais } from '../../contexts/PaisContext';
import { resolverConfigAudiencia } from '../landing/audiencia';
import { elegirPorSesion } from '../landing/visibilidadSesion';
import { useAuth } from '../../contexts/AuthContext';
import NeuralOrbIcon from '../NeuralOrbIcon';

const FooterContainer = styled.footer`
  background: linear-gradient(180deg, #0a2a47 0%, #0f355b 100%);
  padding: 3rem 15px 1.5rem;
  margin-top: auto;
  color: #94a3b8;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterMain = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(20, 182, 203, 0.15);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
`;

const LogoText = styled.span`
  font-size: 1.8rem;
  font-weight: 700;
  color: #ffffff;
  font-family: 'Myriad Pro', sans-serif;
  letter-spacing: -0.5px;
`;

const ContactSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

const ContactLabel = styled.span`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #64748b;
`;

const ContactEmail = styled.a`
  color: #14b6cb;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 600;
  transition: color 0.3s ease;

  &:hover { color: #22d3ee; }
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.5rem;
  font-size: 0.82rem;
  color: #64748b;
  font-family: 'Myriad Pro', sans-serif;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
    padding-bottom: 60px;
  }
`;

const VersionBadge = styled.span`
  background: linear-gradient(135deg, rgba(20, 182, 203, 0.18), rgba(34, 211, 238, 0.12));
  color: #22d3ee;
  padding: 0.22rem 0.7rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.6px;
  border: 1px solid rgba(34, 211, 238, 0.32);
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;

  a {
    color: #64748b;
    text-decoration: none;
    transition: color 0.3s ease;
    &:hover { color: #14b6cb; }
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { proyecto } = useProject();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { idioma } = useIdioma();
  // País/subrol del visitante: el footer global de las páginas internas debe
  // resolver sus variantes igual que en la home (SectionRenderer).
  const { audiencia } = usePais();
  const { user } = useAuth();

  const tl = useCallback((value, fallback = '') => {
    if (value == null) return fallback;
    if (typeof value === 'string') return value || fallback;
    if (typeof value === 'object') return value[idioma] || value['es'] || fallback;
    return fallback;
  }, [idioma]);

  // Footer GLOBAL del proyecto (config_json.secciones_globales tipo 'footer').
  // Si existe, se usa en todas las páginas (branded). Si no, el footer fijo.
  //
  // Puede haber más de uno: se elige el primero que le corresponda a esta
  // persona según `mostrar_si` (uno para visitantes y otro para quien ya entró).
  // Con un único pie sin esa clave, se comporta igual que siempre.
  const cj = proyecto?.config_json || {};
  const pies = (Array.isArray(cj.secciones_globales) ? cj.secciones_globales : []).filter(s => s.tipo === 'footer');
  const piesLanding = (Array.isArray(cj.landing_secciones) ? cj.landing_secciones : []).filter(s => s.tipo === 'footer');
  const footerSec = elegirPorSesion(pies, user) || elegirPorSesion(piesLanding, user);

  if (footerSec) {
    const cfgFooter = resolverConfigAudiencia(footerSec.config || {}, audiencia);
    // Rutas donde el footer va recto (config.rutas_sin_radio: una por línea o
    // separadas por coma). Coincide por prefijo: "/perfil" cubre "/perfil/x".
    const rutas = cfgFooter.rutas_sin_radio;
    const lista = (Array.isArray(rutas) ? rutas : String(rutas || '').split(/[\n,]/))
      .map(r => r.trim()).filter(Boolean);
    const sinRadio = lista.some(r => pathname === r || pathname.startsWith(r.endsWith('/') ? r : r + '/'));

    return (
      <FooterSection
        sinRadio={sinRadio}
        config={cfgFooter}
        proyecto={proyecto}
        openLogin={() => navigate('/login')}
        openRegister={() => navigate('/register')}
        tl={tl}
      />
    );
  }

  return (
    <FooterContainer>
      <FooterContent>
        <FooterMain>
          <LogoSection>
            <NeuralOrbIcon />
            <LogoText>{proyecto?.nombre || 'MentorIA'}</LogoText>
          </LogoSection>
          <ContactSection>
            <ContactLabel>Soporte</ContactLabel>
            <ContactEmail href="mailto:soporte@ateneo.co">soporte@ateneo.co</ContactEmail>
          </ContactSection>
        </FooterMain>
        <FooterBottom>
          <div>
            &copy; {currentYear} Ateneo.co. Todos los derechos reservados.
          </div>
          <FooterLinks>
            <a href="#" onClick={e => e.preventDefault()}>Privacidad</a>
            <a href="#" onClick={e => e.preventDefault()}>Términos</a>
            <VersionBadge>v5.0</VersionBadge>
          </FooterLinks>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
